import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';

// Components
import Hero from '../content/Hero';
import CategoryList from '../content/CategoryList';
import PodList from '../content/PodList';
import NeighborhoodList from '../content/NeighborhoodList';
import CityList from '../content/CityList';
import LogoList from '../content/LogoList';
import Triple from '../content/Triple';
import Meta from '../../shared/Meta';
import CtaImage from '../content/CtaImage';
import FeaturedTrucks from '../FeaturedTrucks';
import CuisineLink from '../content/CuisineLink';
import CateringCta from '../content/CateringCta';
import AnyEvent from '../content/AnyEvent';
import FeaturedBlogPostsScroll from '../blog/FeaturedBlogPostsScroll';
import SimpleCta from '../content/SimpleCta';

// Misc
import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';
import tripleStyles from '../content/Triple.module.postcss';
import cuisineLinkStyles from '../content/CuisineLink.module.postcss';

const structuredData = { home: true };
const featuredTrucks = { featured: true };

function Home() {
  const { city_name, vehicle_type, site_title_spaces, site_title, base_url } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();
  const isPortland = city_name === 'Portland';
  const siteTitle = site_title_spaces.slice(0, -4);

  let heroImg = 'https://static.seattlefoodtruck.com/photos/home-hero.webp';
  if (isPortland) {
    heroImg = 'https://static.seattlefoodtruck.com/photos/portland-home.webp';
  }

  return (
    <div>
      <Meta
        title={`Food Trucks Near Me — Find & Book ${city_name}'s Best Food ${vehicle_type}s`}
        structuredData={structuredData}
        description={`Find food trucks near you in ${city_name}. ${site_title} makes it easy to discover, book, and cater food ${vehicleType}s for corporate events, weddings, office lunches, and festivals. Browse menus, compare quotes, and book today.`}
        canonical='/'
      />
      <Hero photo={heroImg}>
        <h1>Find & Book {city_name}&#39;s Best Food {vehicle_type}s!</h1>
        <p>Craving something delicious? {siteTitle} connects you to the best food trucks in town. See who&#39;s cooking nearby, try something new, and book a truck for your next event.</p>
        <Link to='/explore' className='Button Button--large'>Food {vehicle_type}s Near Me</Link>
      </Hero>

      <div className='Section'>
        <LogoList title='Trusted by Leading Companies' titleClassName='u-textCenter' />
      </div>

      <section className='Section' aria-label='Services'>
        <h2>Bring {city_name}&#39;s Food {vehicle_type}s to You</h2>
        <div className={cn(tripleStyles.Triple_wrapper, tripleStyles.Triple_wrapper__tall)}>
          <Triple
            image='https://static.seattlefoodtruck.com/photos/office.webp'
            isResponsive
            title={<><span aria-hidden='true'>🏢 </span>Office Parks &amp; Campuses</>}
            text='Boost office morale and treat your team to fresh, local eats—right outside your door!'
            link='/services/office-parks'
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/apartments.webp'
            isResponsive
            title={<><span aria-hidden='true'>🏡 </span>Apartments &amp; Neighborhoods</>}
            text={`Ditch the boring delivery. Bring a food ${vehicleType} to your community and make mealtime exciting!`}
            link='/services/residential'
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/breweries.webp'
            isResponsive
            title={<><span aria-hidden='true'>🍻 </span>Breweries &amp; Taprooms</>}
            text={`Keep customers happy (and staying longer) with a rotating lineup of ${city_name}'s best food ${vehicleType}s.`}
            link='/services/breweries'
          />
        </div>
      </section>

      <FeaturedTrucks
        title={`Meet ${city_name}'s Best Food ${vehicleType}s`}
        options={featuredTrucks}
      />

      <section className='Section--extraPadding' aria-label='Catering'>
        <h2 className='u-textCenter'>{siteTitle} Catering – Perfect for Any Event!</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/office2.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3><span aria-hidden='true'>🏢 </span>Office Catering – Treat Your Team to Something Delicious!</h3>

            <p>Skip the boring sandwiches—bring a food {vehicleType} to your office! Whether it&#39;s a corporate lunch, employee appreciation day, or a company party, {city_name} food {vehicleType}s offer fresh, made-to-order meals that keep employees happy and productive. Our {vehicleType}s can set up outside for a fun, outdoor break, or serve indoors for a unique, catered experience. Just tell us what you&#39;re looking for, and we&#39;ll connect you with the best food {vehicleType}s for office catering in {city_name}.</p>

            <div>
              <Link
                to='/food-truck-catering/corporate-catering'
                className='Button'
              >
                Book Office Catering
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/wedding4.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3><span aria-hidden='true'>💍 </span>Wedding Catering – Make Your Big Day Unforgettable!</h3>

            <p>Ditch the predictable catering and give your guests an unforgettable, gourmet food truck experience! Whether you want elegant plated meals, buffet-style service, or late-night snacks, {city_name}&#39;s top food {vehicleType}s can customize a menu to match your wedding theme and dietary needs. Simply submit your request, and we&#39;ll match you with {city_name}&#39;s best food {vehicleType}s for weddings—so you can focus on celebrating!</p>

            <div>
              <Link
                to='/food-truck-catering/wedding-catering'
                className='Button'
              >
                Find Wedding Food {vehicle_type}s
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        { !isPortland &&
          <div className={cn(styles.Cta, styles.Cta__reversed)}>
            <CtaImage image='https://static.seattlefoodtruck.com/photos/community.webp' isResponsive />
            <div className={styles.Cta_copy}>
              <h3><span aria-hidden='true'>🎡 </span>Events & Festivals – Bring the Crowd with Amazing Food!</h3>
              <p>From neighborhood block parties to music festivals, school fundraisers, and grand openings, food {vehicleType}s add energy and variety to any gathering. Whether you need a single {vehicleType} or a full lineup of vendors, we make it easy to find the best food {vehicleType}s for community events in {city_name}. Compare quotes, browse menus, and book your favorites—all in one place.</p>
              <div>
                <Link
                  to='/food-truck-catering/community-events'
                  className='Button'
                >
                  Book Food {vehicle_type}s for Your Event
                </Link>
              </div>
            </div>
          </div>
        }

        { isPortland &&
          <div className={cn(styles.Cta, styles.Cta__reversed)}>
            <CtaImage image='https://static.seattlefoodtruck.com/photos/portland-about.jpeg' />
            <div className={styles.Cta_copy}>
              <h4>About FoodCartsPortland.com</h4>
              <p>Started in 2008, FoodCartsPortland.com offers a practical guide to discover and book food carts, food trucks, food trailers and mobile caterers in Portland, Oregon. From food carts to food trucks, we&#39;re the most comprehensive guide to street food in Portland.</p>
              <p>Help support small, locally-owned businesses by using our interactive map to find where your next meal is parked, or bring the party to you directly by booking one of Portland&#39;s best food trucks for your next event!</p>
            </div>
          </div>
        }
      </section>

      <AnyEvent />

      <section className='Section--extraPadding' aria-label='Popular cuisines'>
        <h3 className='u-phosphate'>Most Popular Food {vehicle_type} Cuisines</h3>
        <div className={cuisineLinkStyles.CuisineLinkContainer}>
          <CuisineLink title='Tacos' emoji='&#127790;' link='/taco-trucks' />
          <CuisineLink title='Sandwiches' emoji='&#129386;' link='/sandwich-food-trucks' />
          <CuisineLink title='Desserts' emoji='&#127846;' link='/dessert-food-trucks' />
          <CuisineLink title='Burgers' emoji='&#127828;' link='/burger-food-trucks' />
          <CuisineLink title='BBQ' emoji='&#127830;' link='/bbq-food-trucks' />
          <CuisineLink title='Breakfast' emoji='&#129374;' link='/breakfast-food-trucks' />
          <CuisineLink title='Pizza' emoji='&#127829;' link='/pizza-food-trucks' />
          <CuisineLink title='Healthy' emoji='&#129367;' link='/healthy-food-trucks' />
          <CuisineLink title='Gyros' emoji='&#129369;' link='/gyro-food-trucks' />
          <CuisineLink title='Asian' emoji='&#129377;' link='/asian-food-trucks' />
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/bookings.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Own a Food {vehicle_type}? Get More Bookings with {siteTitle}!</h3>

            <p>Join {city_name}&#39;s #1 food {vehicleType} network and connect with thousands of hungry customers, event organizers, and businesses looking for top-notch food {vehicleType}s. Whether you want to book more catering gigs, brewery pop-ups, office lunches, or high-traffic vending spots, we help you grow your business, increase visibility, and keep your schedule full—all with zero hassle.</p>

            <div>
              <Link
                to='/vendors'
                className='Button'
              >
                Join {siteTitle} Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className='Section'>
        <FeaturedBlogPostsScroll title={`${siteTitle} Blog – News, Tips & Insider Stories`} />
      </section>

      <section className='Section u-mb2'>
        <SimpleCta
          text={`Discover ${city_name}'s best food ${vehicleType}s, get insider tips, and stay updated on events, catering, and must-try eats!`}
          link='/blog'
          linkTitle='Read The Blog'
        />
      </section>

      <section className='Section'>
        <CateringCta title={<><span aria-hidden='true'>🚚 </span>Book a Food {vehicle_type} for Your Event!</>} />
      </section>

      <hr className='u-mt2' />

      <section className='Section'>
        <NeighborhoodList vehicleType={vehicleType} />
      </section>

      <section className='Section'>
        <CityList cityName={city_name} vehicleType={vehicleType} />
      </section>

      <section className='Section'>
        <CategoryList vehicleType={vehicleType} />
      </section>

      <section className='Section'>
        <PodList cityName={city_name} vehicleType={vehicleType} />
      </section>
    </div>
  );
}

export default Home;
